import React, { useState, useEffect, useCallback } from 'react';
import { useRouter } from '../../../router/Router';
import { useAuth } from '../../../auth/AuthContext';
import {
  StudentDemandRecord,
  StudentDemandFilter,
  StudentDemandSummaryAnalytics,
  UniversityDemandAnalytics,
  AreaDemandAnalytics,
  UniversityAreaMatrixData,
  DemandPlanningSettings,
  DemandPlanningStatus,
} from '../../../types';
import {
  studentDemandService,
  DEFAULT_DEMAND_SETTINGS,
} from '../../../services/studentDemandService';
import { WEEKDAY_OPTIONS } from '../../../constants/alexandriaAreas';
import { DemandKpis } from './DemandKpis';
import { DemandAnalyticsCharts } from './DemandAnalyticsCharts';
import { DemandHeatmapMatrix } from './DemandHeatmapMatrix';
import { RouteOpportunitiesView } from './RouteOpportunitiesView';
import { DemandDataTable } from './DemandDataTable';
import { DemandDataEntryModal } from './DemandDataEntryModal';
import { DemandSettingsModal } from './DemandSettingsModal';
import {
  Plus,
  BarChart3,
  Grid,
  Sparkles,
  Settings,
  RefreshCw,
  Building2,
  MapPin,
  CheckCircle2,
  XCircle,
  Archive,
  Users,
  Lock,
  ShieldCheck,
  AlertTriangle,
  Trash2,
} from 'lucide-react';
import './StudentDemand.css';

type ActiveTab = 'ROSTER' | 'OVERVIEW' | 'OPPORTUNITIES' | 'MATRIX';

export const AdminStudentDemand: React.FC = () => {
  const { navigate } = useRouter();
  const { user } = useAuth();

  // Navigation State
  const [activeTab, setActiveTab] = useState<ActiveTab>('ROSTER');

  // Loading States
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(
    null
  );

  // Data States
  const [records, setRecords] = useState<StudentDemandRecord[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const pageSize = 15;

  const [filter, setFilter] = useState<StudentDemandFilter>({
    status: 'ACTIVE',
  });

  const [summary, setSummary] = useState<StudentDemandSummaryAnalytics>({
    totalDemandStudents: 0,
    activeDemandStudents: 0,
    archivedDemandStudents: 0,
    totalAttendanceDemandDays: 0,
    avgAttendanceDays: 16.0,
    uniqueUniversitiesRequested: 0,
    uniqueAreasRepresented: 0,
    highestDemandUniversity: null,
    highestDemandArea: null,
    highOpportunityRoutesCount: 0,
    governorateBreakdown: {},
    areaGroupBreakdown: {},
    monthlyGrowthTrend: [],
    lastUpdated: new Date().toISOString(),
  });

  const [universityAnalytics, setUniversityAnalytics] = useState<UniversityDemandAnalytics[]>([]);
  const [areaAnalytics, setAreaAnalytics] = useState<AreaDemandAnalytics[]>([]);
  const [matrixData, setMatrixData] = useState<UniversityAreaMatrixData>({
    areas: [],
    universities: [],
    cells: {},
  });

  const [settings, setSettings] = useState<DemandPlanningSettings>(DEFAULT_DEMAND_SETTINGS);
  const [allDemandRecords, setAllDemandRecords] = useState<StudentDemandRecord[]>([]);

  // Modals
  const [isDataEntryOpen, setIsDataEntryOpen] = useState<boolean>(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState<boolean>(false);
  const [selectedRecordForEdit, setSelectedRecordForEdit] = useState<StudentDemandRecord | null>(
    null
  );
  const [archiveReason, setArchiveReason] = useState<string>('');

  // Selected University Drilldown
  const [drilldownUni, setDrilldownUni] = useState<UniversityDemandAnalytics | null>(null);
  const [drilldownArea, setDrilldownArea] = useState<AreaDemandAnalytics | null>(null);

  // Security Passwords & Lock States
  // 1. View Password: 159753 (to view student roster and requests)
  const [isRosterUnlocked, setIsRosterUnlocked] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem('demand_roster_unlocked') === 'true';
    }
    return false;
  });
  const [viewPasswordInput, setViewPasswordInput] = useState<string>('');
  const [viewPasswordError, setViewPasswordError] = useState<string | null>(null);

  // 2. Delete / Archive Security Password: 984512 (to delete or archive any request)
  const [pendingDeleteRecord, setPendingDeleteRecord] = useState<{
    id: string;
    studentName: string;
    type: 'DELETE' | 'ARCHIVE' | 'PURGE';
  } | null>(null);
  const [deletePasswordInput, setDeletePasswordInput] = useState<string>('');
  const [deletePasswordError, setDeletePasswordError] = useState<string | null>(null);

  // Load all analytics data
  const loadAnalytics = useCallback(async () => {
    try {
      const [sumRes, uniRes, areaRes, matrixRes, setRes, allRecordsRes] = await Promise.all([
        studentDemandService.getSummaryAnalytics(),
        studentDemandService.getUniversityDemandAnalytics(),
        studentDemandService.getAreaDemandAnalytics(),
        studentDemandService.getUniversityAreaMatrix(),
        studentDemandService.getSettings(),
        studentDemandService.getAllDemandRecords(),
      ]);

      setSummary(sumRes);
      setUniversityAnalytics(uniRes);
      setAreaAnalytics(areaRes);
      setMatrixData(matrixRes);
      setSettings(setRes);
      setAllDemandRecords(allRecordsRes);
    } catch (err) {
      console.warn('Error loading demand analytics:', err);
    }
  }, []);

  // Load records for table
  const loadRecords = useCallback(
    async (page: number = currentPage, currentFilter: StudentDemandFilter = filter) => {
      try {
        const res = await studentDemandService.queryDemandRecords(currentFilter, page, pageSize);
        setRecords(res.records);
        setTotalCount(res.total);
        setTotalPages(res.totalPages);
        setCurrentPage(res.page);
      } catch (err) {
        console.warn('Error querying demand records:', err);
      }
    },
    [currentPage, filter, pageSize]
  );

  // Initial Load with automatic linking of registered students and trip bookings
  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      try {
        await studentDemandService.syncAndLinkWithStudentsAndBookings();
      } catch (e) {
        console.warn('Initial student demand auto-sync skipped:', e);
      }
      await Promise.all([loadAnalytics(), loadRecords(1, filter)]);
      setIsLoading(false);
    };
    init();
  }, []);

  // Refresh handler
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await Promise.all([loadAnalytics(), loadRecords(currentPage, filter)]);
    setIsRefreshing(false);
    setAlertMessage({ text: 'تم تحديث الإحصائيات وسجل الطلبات بنجاح', type: 'success' });
    setTimeout(() => setAlertMessage(null), 3000);
  };

  // Sync Bookings & Registered Students handler
  const handleSyncBookingsAndStudents = async () => {
    setIsSyncing(true);
    try {
      const res = await studentDemandService.syncAndLinkWithStudentsAndBookings();
      await Promise.all([loadAnalytics(), loadRecords(currentPage, filter)]);
      setAlertMessage({
        text: `تمت مزامنة ${res.syncedStudentsCount} طالب مسجل و ${res.syncedBookingsCount} حجز رحلات، وتحديث ${res.totalUpdated} سجل بخطوط السير ومحطات الركوب بنجاح!`,
        type: 'success',
      });
    } catch (err) {
      console.error('Failed to sync bookings & registered students:', err);
      setAlertMessage({ text: 'حدث خطأ أثناء مزامنة بيانات الطلاب والحجوزات', type: 'error' });
    } finally {
      setIsSyncing(false);
      setTimeout(() => setAlertMessage(null), 4000);
    }
  };

  // Record Created
  const handleRecordCreated = async () => {
    await Promise.all([loadAnalytics(), loadRecords(1, filter)]);
    setAlertMessage({ text: 'تم تسجيل رغبة الطالب وتحديث مصفوفة الطلب بنجاح', type: 'success' });
    setTimeout(() => setAlertMessage(null), 3000);
  };

  // Unlock Roster Handler (Password: 159753)
  const handleUnlockRoster = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (viewPasswordInput.trim() === '159753') {
      setIsRosterUnlocked(true);
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('demand_roster_unlocked', 'true');
      }
      setViewPasswordError(null);
      setViewPasswordInput('');
      setAlertMessage({ text: 'تم التحقق من كلمة المرور وفتح سجل بيانات الطلاب والطلبات بنجاح', type: 'success' });
      setTimeout(() => setAlertMessage(null), 3000);
    } else {
      setViewPasswordError('كلمة المرور غير صحيحة! يرجى إدخال كلمة المرور الصحيحة للاطلاع على السجل.');
    }
  };

  // Lock Roster Handler
  const handleLockRoster = () => {
    setIsRosterUnlocked(false);
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('demand_roster_unlocked');
    }
    setAlertMessage({ text: 'تم قفل سجل بيانات الطلاب بنجاح', type: 'success' });
    setTimeout(() => setAlertMessage(null), 3000);
  };

  // Confirm Delete / Archive / Purge Handler (Password: 984512)
  const handleConfirmDeleteWithPassword = async () => {
    if (!pendingDeleteRecord) return;
    if (deletePasswordInput.trim() !== '984512') {
      setDeletePasswordError('كلمة مرور الحذف غير صحيحة! يرجى إدخال كلمة المرور المعتمدة لتنفيذ هذا الإجراء.');
      return;
    }

    const adminUser = {
      uid: user?.uid || 'admin_omarkhaled',
      email: user?.email || 'omarkhaled20262@gmail.com',
    };

    if (pendingDeleteRecord.type === 'PURGE') {
      await studentDemandService.purgeAllMockRecords();
      await handleRefresh();
      setAlertMessage({
        text: 'تم تنظيف وتصفير كافة السجلات والبيانات بنجاح للتشغيل الفعلي.',
        type: 'success',
      });
    } else if (pendingDeleteRecord.type === 'DELETE') {
      const ok = await studentDemandService.deleteDemandRecord(pendingDeleteRecord.id, adminUser);
      if (ok) {
        setAlertMessage({
          text: `تم حذف طلب الطالب (${pendingDeleteRecord.studentName}) نهائياً بنجاح`,
          type: 'success',
        });
        await Promise.all([loadAnalytics(), loadRecords(currentPage, filter)]);
      } else {
        setAlertMessage({ text: 'حدث خطأ أثناء محاولة حذف الطلب', type: 'error' });
      }
    } else if (pendingDeleteRecord.type === 'ARCHIVE') {
      const ok = await studentDemandService.archiveDemandRecord(
        pendingDeleteRecord.id,
        adminUser,
        archiveReason || 'أرشفة إدارية معتمدة'
      );
      if (ok) {
        setAlertMessage({
          text: `تم نقل طلب الطالب (${pendingDeleteRecord.studentName}) إلى الأرشيف بنجاح`,
          type: 'success',
        });
        await Promise.all([loadAnalytics(), loadRecords(currentPage, filter)]);
      } else {
        setAlertMessage({ text: 'حدث خطأ أثناء محاولة أرشفة الطلب', type: 'error' });
      }
    }

    setPendingDeleteRecord(null);
    setDeletePasswordInput('');
    setDeletePasswordError(null);
    setArchiveReason('');
    setTimeout(() => setAlertMessage(null), 3000);
  };

  // Update Planning Status
  const handleUpdatePlanningStatus = async (
    uniId: string,
    status: DemandPlanningStatus,
    note?: string
  ) => {
    const adminUser = {
      uid: user?.uid || 'admin_omarkhaled',
      email: user?.email || 'omarkhaled20262@gmail.com',
    };

    // Update matching records
    const matching = records.filter((r) => r.universityId === uniId);
    for (const r of matching) {
      await studentDemandService.updatePlanningStatus(r.id, status, adminUser, note);
    }

    await Promise.all([loadAnalytics(), loadRecords(currentPage, filter)]);
    setAlertMessage({ text: `تم تحديث حالة التخطيط إلى (${status}) بنجاح`, type: 'success' });
    setTimeout(() => setAlertMessage(null), 3000);
  };

  // Export CSV
  const handleExportCsv = async () => {
    try {
      const csvContent = await studentDemandService.exportDemandToCsv(filter);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute(
        'download',
        `NASHGO_STUDENT_DEMAND_${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setAlertMessage({ text: 'تم تصدير ملف CSV بنجاح', type: 'success' });
      setTimeout(() => setAlertMessage(null), 3000);
    } catch {
      setAlertMessage({ text: 'حدث خطأ أثناء تصدير ملف CSV', type: 'error' });
      setTimeout(() => setAlertMessage(null), 3000);
    }
  };

  // Save Settings
  const handleSaveSettings = async (newSettings: DemandPlanningSettings) => {
    const adminUser = {
      uid: user?.uid || 'admin_omarkhaled',
      email: user?.email || 'omarkhaled20262@gmail.com',
    };
    const updated = await studentDemandService.updateSettings(newSettings, adminUser);
    setSettings(updated);
    await loadAnalytics();
    setAlertMessage({ text: 'تم حفظ معايير التقييم التشغيلي بنجاح', type: 'success' });
    setTimeout(() => setAlertMessage(null), 3000);
  };

  return (
    <div className="demand-module-container" dir="rtl">
      {/* Module Header */}
      <div className="demand-header">
        <div>
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span className="demand-header-badge live">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              <span>نظام الذكاء التشغيلي المباشر</span>
            </span>
            <span className="demand-header-badge">Operations Intelligence & Data Entry</span>
          </div>

          <h1 className="demand-header-title">
            <span>بيانات طلبات الرحلات (Student Demand)</span>
          </h1>

          <p className="demand-header-subtitle">
            تسجيل رغبات الطلاب الجامعيين وتحليل حجم الطلب، أيام الحضور، ونقاط التجمع بالإسكندرية لدعم قرارات تدشين الخطوط.
          </p>
        </div>

        {/* Top Header Actions */}
        <div className="demand-header-actions">
          <button
            type="button"
            className="btn-admin-outline text-xs py-2 px-3 flex items-center gap-1.5 text-blue-700 border-blue-300 hover:bg-blue-50"
            onClick={handleSyncBookingsAndStudents}
            disabled={isSyncing}
            title="مزامنة بيانات الطلاب المسجلين وحجوزات الرحلات وربط أيام السفر وخطوط السير ومحطات الركوب"
          >
            <RefreshCw size={14} className={isSyncing ? 'animate-spin' : ''} />
            <span>{isSyncing ? 'جارٍ المزامنة...' : 'مزامنة الحجوزات والطلاب'}</span>
          </button>

          <button
            type="button"
            className="btn-admin-outline text-xs py-2 px-3 flex items-center gap-1.5"
            onClick={handleRefresh}
            disabled={isRefreshing}
            title="تحديث البيانات"
          >
            <RefreshCw size={14} className={isRefreshing ? 'animate-spin' : ''} />
            <span>تحديث</span>
          </button>

          <button
            type="button"
            className="btn-admin-outline text-xs py-2 px-3 flex items-center gap-1.5"
            onClick={() => setIsSettingsOpen(true)}
            title="إعدادات المعايير وعتبات الجدوى"
          >
            <Settings size={14} />
            <span>المعايير التشغيلية</span>
          </button>

          <button
            type="button"
            className="btn-admin-outline text-xs py-2 px-3 flex items-center gap-1.5 text-rose-700 border-rose-300 hover:bg-rose-50"
            onClick={() => {
              setPendingDeleteRecord({
                id: 'ALL_MOCK',
                studentName: 'كافة السجلات المؤقتة',
                type: 'PURGE',
              });
            }}
            title="تطهير ومسح السجلات الوهمية للتشغيل الفعلي (يتطلب كلمة المرور 984512)"
          >
            <Archive size={14} />
            <span>تصفير للتشغيل الفعلي</span>
          </button>

          <button
            type="button"
            className="btn-primary text-xs py-2.5 px-4 font-bold flex items-center gap-1.5 shadow-sm"
            onClick={() => setIsDataEntryOpen(true)}
          >
            <Plus size={16} />
            <span>تسجيل طلب جديد (Data Entry)</span>
          </button>
        </div>
      </div>

      {/* Alert Banner */}
      {alertMessage && (
        <div
          className={`p-3 rounded-xl mb-4 text-xs font-bold flex items-center gap-2 ${
            alertMessage.type === 'success'
              ? 'bg-emerald-50 border border-emerald-300 text-emerald-800'
              : 'bg-red-50 border border-red-300 text-red-800'
          }`}
        >
          {alertMessage.type === 'success' ? (
            <CheckCircle2 size={16} />
          ) : (
            <XCircle size={16} />
          )}
          <span>{alertMessage.text}</span>
        </div>
      )}

      {/* Top KPIs Summary (Sections 17 & 38 Visual Priority) */}
      <DemandKpis summary={summary} />

      {/* Navigation Tabs Bar */}
      <nav className="demand-tabs-nav">
        <button
          type="button"
          className={`demand-tab-btn ${activeTab === 'ROSTER' ? 'active' : ''}`}
          onClick={() => setActiveTab('ROSTER')}
        >
          <Users size={16} />
          <span>سجل بيانات الطلاب ورغبات النقل</span>
          <span className="demand-tab-badge">{summary.totalDemandStudents || totalCount}</span>
        </button>

        <button
          type="button"
          className={`demand-tab-btn ${activeTab === 'OVERVIEW' ? 'active' : ''}`}
          onClick={() => setActiveTab('OVERVIEW')}
        >
          <BarChart3 size={16} />
          <span>لوحة التحليلات والرسوم البيانية</span>
        </button>

        <button
          type="button"
          className={`demand-tab-btn ${activeTab === 'OPPORTUNITIES' ? 'active' : ''}`}
          onClick={() => setActiveTab('OPPORTUNITIES')}
        >
          <Sparkles size={16} />
          <span>فرص تدشين الخطوط (Route Opportunities)</span>
          <span className="demand-tab-badge">{summary.highOpportunityRoutesCount}</span>
        </button>

        <button
          type="button"
          className={`demand-tab-btn ${activeTab === 'MATRIX' ? 'active' : ''}`}
          onClick={() => setActiveTab('MATRIX')}
        >
          <Grid size={16} />
          <span>مصفوفة المناطق والجامعات (Heatmap)</span>
        </button>
      </nav>

      {/* Tab 1: Students Data Registry & Roster */}
      {activeTab === 'ROSTER' && (
        !isRosterUnlocked ? (
          <div className="bg-white rounded-3xl border border-slate-200/80 shadow-lg p-10 text-center max-w-md mx-auto my-12 backdrop-blur-sm">
            <div className="w-16 h-16 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center mx-auto mb-4 border border-amber-200 shadow-inner">
              <Lock size={32} />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">سجل طلبات الطلاب محمي بكلمة مرور</h2>
            <p className="text-xs text-slate-500 mb-6 leading-relaxed">
              للاطلاع على بيانات الطلاب المسجلين وسجل رغبات النقل والطلبات، يرجى إدخال كلمة المرور المعتمدة.
            </p>

            <form onSubmit={handleUnlockRoster} className="space-y-4">
              <div>
                <input
                  type="password"
                  value={viewPasswordInput}
                  onChange={(e) => {
                    setViewPasswordInput(e.target.value);
                    setViewPasswordError(null);
                  }}
                  placeholder="أدخل كلمة المرور"
                  className="w-full text-center tracking-widest text-lg font-mono px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all shadow-sm"
                  autoFocus
                />
                {viewPasswordError && (
                  <p className="text-xs font-bold text-rose-600 mt-2 flex items-center justify-center gap-1.5">
                    <XCircle size={14} />
                    <span>{viewPasswordError}</span>
                  </p>
                )}
              </div>

              <button
                type="submit"
                className="w-full btn-primary py-3 rounded-xl font-bold text-xs flex items-center justify-center gap-2 shadow-sm"
              >
                <ShieldCheck size={16} />
                <span>فتح السجل والاطلاع على الطلاب والطلبات</span>
              </button>
            </form>
          </div>
        ) : (
          <DemandDataTable
            records={records}
            totalCount={totalCount}
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            isLoading={isLoading}
            filter={filter}
            onFilterChange={(newFilter) => {
              setFilter(newFilter);
              loadRecords(1, newFilter);
            }}
            onPageChange={(p) => loadRecords(p, filter)}
            onEditRecord={(r) => setSelectedRecordForEdit(r)}
            onArchiveRecord={(rId, sName) =>
              setPendingDeleteRecord({
                id: rId,
                studentName: sName || 'طالب',
                type: 'ARCHIVE',
              })
            }
            onDeleteRecord={(rId, sName) =>
              setPendingDeleteRecord({
                id: rId,
                studentName: sName,
                type: 'DELETE',
              })
            }
            onLockRoster={handleLockRoster}
            onExportCsv={handleExportCsv}
            onRefresh={handleRefresh}
            onOpenDataEntry={() => setIsDataEntryOpen(true)}
          />
        )
      )}

      {/* Tab 2: Overview & Analytics */}
      {activeTab === 'OVERVIEW' && (
        <DemandAnalyticsCharts
          universities={universityAnalytics}
          areas={areaAnalytics}
          summary={summary}
          allDemandRecords={allDemandRecords}
          onSelectUniversity={(uniId) => {
            const found = universityAnalytics.find((u) => u.universityId === uniId);
            if (found) setDrilldownUni(found);
          }}
          onSelectArea={(areaId) => {
            const found = areaAnalytics.find((a) => a.areaId === areaId);
            if (found) setDrilldownArea(found);
          }}
        />
      )}

      {/* Tab 3: Route Opportunities */}
      {activeTab === 'OPPORTUNITIES' && (
        <RouteOpportunitiesView
          universities={universityAnalytics}
          settings={settings}
          onUpdateStatus={handleUpdatePlanningStatus}
          onScheduleTripClick={() => navigate('/admin/trips')}
        />
      )}

      {/* Tab 4: University x Area Matrix */}
      {activeTab === 'MATRIX' && (
        <DemandHeatmapMatrix
          matrixData={matrixData}
          onCellClick={(_areaId, universityId) => {
            const foundUni = universityAnalytics.find((u) => u.universityId === universityId);
            if (foundUni) setDrilldownUni(foundUni);
          }}
        />
      )}

      {/* Modal 1: Fast Data Entry Modal */}
      <DemandDataEntryModal
        isOpen={isDataEntryOpen}
        onClose={() => setIsDataEntryOpen(false)}
        onRecordCreated={handleRecordCreated}
        onOpenExistingRecord={(r) => {
          setSelectedRecordForEdit(r);
        }}
      />

      {/* Modal 2: Operational Thresholds Settings Modal */}
      <DemandSettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        currentSettings={settings}
        onSave={handleSaveSettings}
      />

      {/* Modal: Password Protected Delete / Archive / Purge Modal (Password: 984512) */}
      {pendingDeleteRecord && (
        <div
          className="demand-modal-overlay"
          onClick={() => {
            setPendingDeleteRecord(null);
            setDeletePasswordInput('');
            setDeletePasswordError(null);
          }}
        >
          <div className="demand-modal-box max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="demand-modal-header bg-rose-50 border-b border-rose-100">
              <div className="flex items-center gap-2 text-rose-700">
                <AlertTriangle size={20} />
                <h3 className="demand-modal-title text-rose-900">
                  {pendingDeleteRecord.type === 'DELETE'
                    ? 'تأكيد حذف طلب الطالب نهائياً'
                    : pendingDeleteRecord.type === 'PURGE'
                    ? 'تأكيد تصفير السجلات للتشغيل الفعلي'
                    : 'تأكيد أرشفة طلب الطالب'}
                </h3>
              </div>
              <button
                className="demand-modal-close-btn"
                onClick={() => {
                  setPendingDeleteRecord(null);
                  setDeletePasswordInput('');
                  setDeletePasswordError(null);
                }}
              >
                ✕
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900 leading-relaxed">
                <strong>حماية أمنية مشددة:</strong> لا يمكن حذف أو أرشفة أي طلب من المنظومة إلا بإدخال كلمة مرور الحذف المعتمدة لإدارة النظام.
              </div>

              <p className="text-xs text-slate-700 leading-relaxed">
                {pendingDeleteRecord.type === 'PURGE' ? (
                  <span>هل أنت متأكد من تصفير كافة السجلات والبدء النظيف للتشغيل الفعلي؟</span>
                ) : (
                  <span>
                    هل أنت متأكد من {pendingDeleteRecord.type === 'DELETE' ? 'حذف' : 'أرشفة'} طلب الطالب{' '}
                    <strong className="text-slate-900 font-bold">"{pendingDeleteRecord.studentName}"</strong>؟
                  </span>
                )}
              </p>

              {pendingDeleteRecord.type === 'ARCHIVE' && (
                <div className="demand-form-group">
                  <label className="demand-form-label">سبب الأرشفة (اختياري)</label>
                  <input
                    type="text"
                    placeholder="مثال: حجز الطالب في رحلة، إلغاء الرغبة..."
                    value={archiveReason}
                    onChange={(e) => setArchiveReason(e.target.value)}
                    className="demand-form-input text-xs"
                  />
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-slate-800 mb-1.5">
                  كلمة مرور الحذف (Password):
                </label>
                <input
                  type="password"
                  value={deletePasswordInput}
                  onChange={(e) => {
                    setDeletePasswordInput(e.target.value);
                    setDeletePasswordError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleConfirmDeleteWithPassword();
                    }
                  }}
                  placeholder="أدخل كلمة مرور الحذف"
                  className="w-full text-center tracking-widest text-lg font-mono px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-rose-500 outline-none shadow-sm"
                  autoFocus
                />
                {deletePasswordError && (
                  <p className="text-xs font-bold text-rose-600 mt-2 flex items-center gap-1.5">
                    <XCircle size={14} />
                    <span>{deletePasswordError}</span>
                  </p>
                )}
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  className="btn-admin-outline text-xs px-4 py-2"
                  onClick={() => {
                    setPendingDeleteRecord(null);
                    setDeletePasswordInput('');
                    setDeletePasswordError(null);
                  }}
                >
                  إلغاء
                </button>
                <button
                  type="button"
                  className="btn-admin-danger text-xs px-5 py-2 font-bold flex items-center gap-1.5"
                  onClick={handleConfirmDeleteWithPassword}
                >
                  <Trash2 size={14} />
                  <span>
                    تأكيد {pendingDeleteRecord.type === 'DELETE' ? 'الحذف النهائي' : pendingDeleteRecord.type === 'PURGE' ? 'التصفير' : 'الأرشفة'}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal 3.5: Edit Record Modal */}
      {selectedRecordForEdit && (
        <div
          className="demand-modal-overlay"
          onClick={() => setSelectedRecordForEdit(null)}
        >
          <div className="demand-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="demand-modal-header">
              <h3 className="demand-modal-title">
                <span>تعديل رغبة الطالب</span>
              </h3>
              <button
                className="demand-modal-close-btn"
                onClick={() => setSelectedRecordForEdit(null)}
              >
                ✕
              </button>
            </div>

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const adminUser = {
                  uid: user?.uid || 'admin_omarkhaled',
                  email: user?.email || 'omarkhaled20262@gmail.com',
                };
                const ok = await studentDemandService.updateDemandRecord(
                  selectedRecordForEdit.id,
                  {
                    studentName: selectedRecordForEdit.studentName,
                    phone: selectedRecordForEdit.phone,
                    notes: selectedRecordForEdit.notes,
                    attendanceDaysPerMonth: selectedRecordForEdit.attendanceDaysPerMonth,
                    preferredTravelDays: selectedRecordForEdit.preferredTravelDays,
                    planningStatus: selectedRecordForEdit.planningStatus,
                  },
                  adminUser
                );
                if (ok.success) {
                  setSelectedRecordForEdit(null);
                  await Promise.all([loadAnalytics(), loadRecords(currentPage, filter)]);
                  setAlertMessage({ text: 'تم تحديث بيانات الطلب بنجاح', type: 'success' });
                  setTimeout(() => setAlertMessage(null), 3000);
                }
              }}
              className="space-y-4 text-xs"
            >
              <div className="demand-form-group">
                <label className="demand-form-label">اسم الطالب</label>
                <input
                  type="text"
                  value={selectedRecordForEdit.studentName}
                  onChange={(e) =>
                    setSelectedRecordForEdit({
                      ...selectedRecordForEdit,
                      studentName: e.target.value,
                    })
                  }
                  className="demand-form-input text-xs"
                  required
                />
              </div>

              <div className="demand-form-group">
                <label className="demand-form-label">رقم الهاتف</label>
                <input
                  type="tel"
                  dir="ltr"
                  value={selectedRecordForEdit.phone}
                  onChange={(e) =>
                    setSelectedRecordForEdit({
                      ...selectedRecordForEdit,
                      phone: e.target.value,
                    })
                  }
                  className="demand-form-input text-xs font-mono"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="demand-form-group">
                  <label className="demand-form-label">أيام الحضور شهرياً</label>
                  <input
                    type="number"
                    min={1}
                    max={31}
                    value={selectedRecordForEdit.attendanceDaysPerMonth || ''}
                    onChange={(e) =>
                      setSelectedRecordForEdit({
                        ...selectedRecordForEdit,
                        attendanceDaysPerMonth: e.target.value ? Number(e.target.value) : null,
                      })
                    }
                    className="demand-form-input text-xs font-mono"
                  />
                </div>

                <div className="demand-form-group">
                  <label className="demand-form-label">حالة التخطيط التشغيلي</label>
                  <select
                    value={selectedRecordForEdit.planningStatus}
                    onChange={(e) =>
                      setSelectedRecordForEdit({
                        ...selectedRecordForEdit,
                        planningStatus: e.target.value as DemandPlanningStatus,
                      })
                    }
                    className="demand-form-select text-xs"
                  >
                    <option value="NEW">جديد</option>
                    <option value="MONITORING">قيد المراقبة</option>
                    <option value="HIGH_DEMAND">طلب مرتفع</option>
                    <option value="ROUTE_PLANNING">قيد التخطيط</option>
                    <option value="ROUTE_CREATED">تمت الجدولة</option>
                    <option value="ACTIVE_ROUTE">خط نشط</option>
                    <option value="CLOSED">مغلق</option>
                  </select>
                </div>
              </div>

              <div className="demand-form-group">
                <label className="demand-form-label">أيام السفر الأسبوعية المحددة للجامعة</label>
                <div className="weekday-chips-grid">
                  {WEEKDAY_OPTIONS.map((dayOpt) => {
                    const days = selectedRecordForEdit.preferredTravelDays || [];
                    const isSelected = days.includes(dayOpt.id);
                    return (
                      <button
                        key={dayOpt.id}
                        type="button"
                        className={`weekday-chip ${isSelected ? 'selected' : ''}`}
                        onClick={() => {
                          const updated = isSelected
                            ? days.filter((d) => d !== dayOpt.id)
                            : [...days, dayOpt.id];
                          setSelectedRecordForEdit({
                            ...selectedRecordForEdit,
                            preferredTravelDays: updated,
                          });
                        }}
                      >
                        <span>{isSelected ? '✓' : '+'}</span>
                        <span>{dayOpt.labelAr}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="demand-form-group">
                <label className="demand-form-label">ملاحظات إضافية</label>
                <textarea
                  rows={2}
                  value={selectedRecordForEdit.notes || ''}
                  onChange={(e) =>
                    setSelectedRecordForEdit({
                      ...selectedRecordForEdit,
                      notes: e.target.value,
                    })
                  }
                  className="demand-form-textarea text-xs"
                />
              </div>

              <div className="demand-modal-actions pt-2">
                <button
                  type="button"
                  className="btn-admin-outline"
                  onClick={() => setSelectedRecordForEdit(null)}
                >
                  إلغاء
                </button>
                <button type="submit" className="btn-primary font-bold">
                  حفظ التعديلات
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal 4: University Details Drilldown Modal */}
      {drilldownUni && (
        <div className="demand-modal-overlay" onClick={() => setDrilldownUni(null)}>
          <div className="demand-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="demand-modal-header">
              <div>
                <h3 className="demand-modal-title">
                  <Building2 size={20} className="text-blue-600" />
                  <span>{drilldownUni.universityName}</span>
                </h3>
                <span className="text-xs text-slate-500">
                  محافظة {drilldownUni.governorate} — تفاصيل تركز الطلب التشغيلي
                </span>
              </div>
              <button className="demand-modal-close-btn" onClick={() => setDrilldownUni(null)}>
                ✕
              </button>
            </div>

            <div className="space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 rounded-xl border">
                <div>
                  <span className="text-slate-500 block">إجمالي الطلاب الراغبين</span>
                  <strong className="text-base text-slate-900 font-mono">
                    {drilldownUni.activeStudents} طالب نشط
                  </strong>
                </div>
                <div>
                  <span className="text-slate-500 block">حجم الحضور التقديري</span>
                  <strong className="text-base text-emerald-700 font-mono">
                    {drilldownUni.totalAttendanceDemand.toLocaleString('ar-EG')} يوم شهرياً
                  </strong>
                </div>
                <div>
                  <span className="text-slate-500 block">سعة أسطول الحافلات (50 راكباً)</span>
                  <strong className="text-slate-800 font-mono">
                    ~{Math.ceil(drilldownUni.activeStudents / 50) || 1} حافلة
                  </strong>
                </div>
                <div>
                  <span className="text-slate-500 block">متوسط الحضور للطالب</span>
                  <strong className="text-slate-800 font-mono">
                    {drilldownUni.avgAttendanceDays} يوم شهرياً
                  </strong>
                </div>
              </div>

              <div>
                <h4 className="font-bold text-slate-800 text-xs mb-2">
                  توزيع الطلاب حسب مناطق الإسكندرية:
                </h4>
                <div className="space-y-1.5">
                  {drilldownUni.topAreas.map((a) => (
                    <div
                      key={a.areaId}
                      className="flex justify-between items-center p-2 rounded-lg bg-white border"
                    >
                      <div>
                        <strong className="text-slate-800">{a.areaName}</strong>
                        <span className="text-slate-400 mr-1.5">({a.areaGroupName})</span>
                      </div>
                      <div className="flex items-center gap-2 font-mono">
                        <span className="text-blue-600 font-bold">{a.percentage}%</span>
                        <strong className="text-slate-800">{a.studentCount} طلاب</strong>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl text-blue-900">
                <strong>التوصية التشغيلية: </strong>
                {drilldownUni.recommendation}
              </div>
            </div>

            <div className="demand-modal-actions mt-4">
              <button
                type="button"
                className="btn-primary text-xs"
                onClick={() => {
                  setDrilldownUni(null);
                  navigate('/admin/trips');
                }}
              >
                جدولة رحلة في النظام
              </button>
              <button
                type="button"
                className="btn-admin-outline text-xs"
                onClick={() => setDrilldownUni(null)}
              >
                إغلاق
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal 5: Area Details Drilldown Modal */}
      {drilldownArea && (
        <div className="demand-modal-overlay" onClick={() => setDrilldownArea(null)}>
          <div className="demand-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="demand-modal-header">
              <div>
                <h3 className="demand-modal-title">
                  <MapPin size={20} className="text-emerald-600" />
                  <span>منطقة {drilldownArea.areaName}</span>
                </h3>
                <span className="text-xs text-slate-500">
                  قطاع {drilldownArea.areaGroupName} — توزيع رغبات طلاب المنطقة
                </span>
              </div>
              <button className="demand-modal-close-btn" onClick={() => setDrilldownArea(null)}>
                ✕
              </button>
            </div>

            <div className="space-y-4 text-xs">
              <div className="p-3 bg-slate-50 rounded-xl border flex justify-between items-center">
                <div>
                  <span className="text-slate-500 block">إجمالي طلاب المنطقة</span>
                  <strong className="text-base text-slate-900 font-mono">
                    {drilldownArea.activeStudents} طالب
                  </strong>
                </div>
                <div>
                  <span className="text-slate-500 block">حجم الحضور الشهري</span>
                  <strong className="text-base text-emerald-700 font-mono">
                    {drilldownArea.totalAttendanceDemand.toLocaleString('ar-EG')} يوم
                  </strong>
                </div>
              </div>

              <div>
                <h4 className="font-bold text-slate-800 text-xs mb-2">
                  الجامعات الأكثر طلباً لطلاب هذه المنطقة:
                </h4>
                <div className="space-y-1.5">
                  {drilldownArea.topUniversities.map((u) => (
                    <div
                      key={u.universityId}
                      className="flex justify-between items-center p-2 rounded-lg bg-white border"
                    >
                      <div>
                        <strong className="text-slate-800">{u.universityName}</strong>
                        <span className="text-slate-400 mr-1.5">({u.governorate})</span>
                      </div>
                      <div className="flex items-center gap-2 font-mono">
                        <span className="text-emerald-600 font-bold">{u.percentage}%</span>
                        <strong className="text-slate-800">{u.studentCount} طلاب</strong>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="demand-modal-actions mt-4">
              <button
                type="button"
                className="btn-admin-outline text-xs"
                onClick={() => setDrilldownArea(null)}
              >
                إغلاق
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminStudentDemand;
